import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FaCheck, FaPrint, FaFileExcel } from 'react-icons/fa';
import { Modal } from './Modals';
import { CLEANABILITY_RATINGS, scoreAnswers, answersFromRecord, cleanabilityChecklistHTML, exportCleanabilityChecklistXlsx } from '../utils/cleanabilityAssessment';
import { printAssessmentReport } from '../utils/assessmentReports';

const RATING_COLORS = {
  Easy: { bg: 'var(--success-bg)', text: 'var(--success-text)' },
  Medium: { bg: 'var(--warning-bg)', text: 'var(--warning-text)' },
  Difficult: { bg: 'var(--danger-bg)', text: 'var(--danger-text)' },
};

export const RatingBadge = ({ rating }) => (
  <span style={{ padding: '2px 10px', borderRadius: '999px', fontWeight: 700, fontSize: 'var(--fs-sm)', background: RATING_COLORS[rating]?.bg || 'var(--bg-color)', color: RATING_COLORS[rating]?.text || 'var(--text-secondary)' }}>
    {rating || '—'}
  </span>
);

// "Help me rate" (API Master rating, and the product-level override in Product
// Master): answer a few questions about how the residue behaves in cleaning; the
// total suggests Easy / Medium / Difficult. The user accepts it or picks another
// rating with a written reason. onApply receives
// { answers, final_rating, override_reason, total, max, suggested } — the form
// keeps it until the record is e-signed and saved; the server re-scores it.
// subject = { heading, rows: [[label, value]], fileName } heads the exported checklist.
const CleanabilityAssessmentModal = ({ isOpen, onClose, config, initial, readOnly = false, apiLabel = '', subject, onApply }) => {
  const [answers, setAnswers] = useState({});
  const [finalRating, setFinalRating] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    // initial is either a saved record (answers hold { option_index, ... }) or a not-yet-saved submission (answers hold indexes)
    setAnswers(answersFromRecord(initial));
    setFinalRating(initial?.final_rating || '');
    setReason(initial?.override_reason || '');
  }, [isOpen, initial]);

  const score = useMemo(() => scoreAnswers(config, answers), [config, answers]);
  const effectiveFinal = finalRating || score.suggested || '';
  const overridden = score.complete && effectiveFinal && effectiveFinal !== score.suggested;

  // Export what is on screen: the saved record (keeps who assessed it and when)
  // while its answers are untouched, otherwise the live answers — blank if none.
  const checklistRecord = () => {
    const savedAnswers = answersFromRecord(initial);
    const untouched = initial?.assessed_at && JSON.stringify(savedAnswers) === JSON.stringify(Object.fromEntries(Object.entries(answers).map(([k, v]) => [k, Number(v)])))
      && effectiveFinal === initial.final_rating;
    if (untouched) return initial;
    return {
      answers,
      suggested: score.suggested || '',
      final_rating: score.complete ? effectiveFinal : '',
      override_reason: overridden ? reason.trim() : '',
    };
  };
  const checklistSubject = subject || { heading: 'Subject', rows: [['Item', apiLabel]] };
  const printChecklist = () => printAssessmentReport({
    reportKey: 'cleanability_assessment',
    title: `Cleanability Checklist${apiLabel ? ` - ${apiLabel}` : ''}`,
    documentName: 'Cleanability Rating Checklist',
    build: () => cleanabilityChecklistHTML(checklistSubject, config, checklistRecord()),
  });
  const exportChecklist = () => exportCleanabilityChecklistXlsx(checklistSubject, config, checklistRecord(), checklistSubject.fileName || `Cleanability_Checklist_${apiLabel}`)
    .catch(() => toast.error('Could not export the checklist'));

  const apply = () => {
    if (!score.complete) return toast.warning(`Answer all ${config.questions.length} questions (${score.answered} answered)`);
    if (overridden && !reason.trim()) return toast.warning(`The helper suggests "${score.suggested}" — give a reason for rating it "${effectiveFinal}"`);
    onApply({
      answers,
      final_rating: effectiveFinal,
      override_reason: overridden ? reason.trim() : '',
      total: score.total,
      max: score.max,
      suggested: score.suggested,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Cleanability scoring helper${apiLabel ? ` — ${apiLabel}` : ''}`}
      size="lg"
      zIndex={1300}
      footer={
        <>
          <button className="btn btn-outline" onClick={printChecklist} title="Print or save as PDF. Prints blank if nothing is answered yet." style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaPrint /> Print checklist
          </button>
          <button className="btn btn-outline" onClick={exportChecklist} title="Download the checklist as an Excel sheet" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: 'auto' }}>
            <FaFileExcel /> Excel
          </button>
          <button className="btn btn-outline" onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</button>
          {!readOnly && (
            <button className="btn btn-primary" onClick={apply} disabled={!score.complete} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaCheck /> Use rating {effectiveFinal ? `"${effectiveFinal}"` : ''}
            </button>
          )}
        </>
      }
    >
      <div style={{ background: 'var(--info-bg)', border: '1.5px solid var(--info-border)', borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', fontSize: 'var(--fs-md)', color: 'var(--info-text)' }}>
        Answer for the residue as it behaves on your equipment with your cleaning process. Each answer scores 1–3; the total
        suggests a rating (≤ {config.bands.easy_max} Easy, ≤ {config.bands.medium_max} Medium, above that Difficult).
        A lab cleanability (coupon) study, where you have one, is stronger evidence. Attach it as the Cleanability Document.
      </div>

      <fieldset disabled={readOnly} style={{ border: 'none', padding: 0, margin: 0 }}>
        {config.questions.map((question, qi) => (
          <div key={question.key} style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px 12px', marginBottom: '8px', background: 'var(--card-bg)' }}>
            <div style={{ fontWeight: 700, fontSize: 'var(--fs-md)', marginBottom: '6px' }}>{qi + 1}. {question.label}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px' }}>
              {question.options.map((option, oi) => (
                <label key={oi} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: readOnly ? 'default' : 'pointer', fontSize: 'var(--fs-md)' }}>
                  <input
                    type="radio"
                    name={`clean-q-${question.key}`}
                    checked={Number(answers[question.key]) === oi && answers[question.key] !== undefined}
                    onChange={() => setAnswers((a) => ({ ...a, [question.key]: oi }))}
                  />
                  {option.label}
                  <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-xs)' }}>({option.score})</span>
                </label>
              ))}
            </div>
          </div>
        ))}

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px', border: '1.5px solid var(--border-color)', borderRadius: '10px', padding: '12px 14px', marginTop: '12px' }}>
          <div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>Score</div>
            <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 700 }}>{score.total} <span style={{ fontSize: 'var(--fs-md)', color: 'var(--text-secondary)' }}>/ {score.max}</span></div>
          </div>
          <div>
            <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>Suggested</div>
            <div style={{ marginTop: '4px' }}>{score.complete ? <RatingBadge rating={score.suggested} /> : <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-md)' }}>{score.answered}/{config.questions.length} answered</span>}</div>
          </div>
          <div style={{ marginLeft: 'auto', minWidth: '200px' }}>
            <label className="form-label" style={{ fontSize: 'var(--fs-md)' }}>Final rating</label>
            <select className="form-control form-control-sm" value={effectiveFinal} onChange={(e) => setFinalRating(e.target.value)} disabled={!score.complete}>
              <option value="">— answer all questions —</option>
              {CLEANABILITY_RATINGS.map((r) => <option key={r} value={r}>{r}{r === score.suggested ? ' (suggested)' : ''}</option>)}
            </select>
          </div>
        </div>

        {overridden && (
          <div style={{ marginTop: '10px' }}>
            <label className="form-label" style={{ fontSize: 'var(--fs-md)', color: 'var(--warning-text)' }}>
              Reason for not using the suggested rating ("{score.suggested}") *
            </label>
            <textarea className="form-control form-control-sm" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Coupon study QC/CS/024 shows complete removal with water in 3 min" />
          </div>
        )}
      </fieldset>
    </Modal>
  );
};

export default CleanabilityAssessmentModal;
